import './style.css';
import { createRoom, joinRoom, type RoomClaim, type Seat } from './api.ts';
import { isEditPaintedMessage, isTvSeatStatusMessage, type CameraCalibrationMessage, type PoseSource, type TvSeatStatusMessage } from './protocol.ts';
import { connectSignalling, relaySignal, type SignalMessage } from './signalling.ts';
import { setCameraCalibration } from './state.ts';
import { mountTvPicker } from './tvPicker.ts';
import { mountCalibrationUi } from './pose/calibrationUi.ts';
import type { CameraCapture } from './media/cameraCapture.ts';
import { FloorPeer } from './media/floorPeer.ts';
import { mountPersonStage } from './floor/personStage.ts';
import { connectBoard } from './board/session.ts';
import { mountBoardEditor } from './board/editor.ts';
import { mountTvBoard } from './board/tv.ts';
import { ClockSynchronizer, epochNow } from './latency/clock.ts';
import { LatencyTracker } from './latency/tracker.ts';
import { mountLatencyHud } from './latency/hud.ts';
import { mountRtcStatsHud } from './media/rtcStatsHud.ts';
import { startDisplaySeatLifecycle, type WakeLockHandle } from './tv/displaySeat.ts';

const appRoot = document.querySelector<HTMLElement>('#app');
if (!appRoot) throw new Error('App root missing');
const app: HTMLElement = appRoot;

const route = location.pathname.replace(/\/+$/, '') || '/';

function shell(seat: string, body: string): void {
  app.innerHTML = `
    <section class="seat-shell">
      <header><a href="/" class="brand">MIRROR LOOP</a><span class="seat-label">${seat}</span></header>
      <div class="seat-body">${body}</div>
    </section>`;
}

function showConnection(claim: RoomClaim, receive?: (message: SignalMessage) => void): WebSocket {
  const status = document.querySelector<HTMLElement>('[data-status]');
  return connectSignalling(claim, (message) => {
    if (status) {
      if (message.type === 'welcome') status.textContent = `Connected · ${message.peers.length} other seat${message.peers.length === 1 ? '' : 's'}`;
      if (message.type === 'peer-joined') status.textContent = `${message.seat.toUpperCase()} joined`;
      if (message.type === 'peer-left') status.textContent = `${message.seat.toUpperCase()} left`;
    }
    receive?.(message);
  });
}

async function floor(): Promise<void> {
  shell('FLOOR', '<div class="floor-seat"><div class="hero"><p class="eyebrow">ONE PRIVATE, IN-MEMORY ROOM</p><h1>Creating a room…</h1><p data-status>Connecting</p><div data-tv-picker></div><div class="pose-readout" data-tv-seat-status>TV seat is not connected.</div><div class="pose-readout" data-camera-pose>Camera not calibrated yet.</div><div class="pose-readout" data-edit-latency>TV paint acknowledgement pending.</div><div data-latency-hud></div><div data-floor-rtc-stats></div></div><div data-person-stage></div><div data-floor-board></div></div>');
  const latency = new LatencyTracker();
  let floorClock: ClockSynchronizer | undefined;
  let floorPeer: FloorPeer | undefined;
  let desiredPoseSource: PoseSource = 'data-channel';
  const stageHost = document.querySelector<HTMLElement>('[data-person-stage]');
  const personStage = stageHost ? mountPersonStage(stageHost, {
    latency,
    floorClockOffsetMs: () => floorClock?.offsetMs ?? 0,
    floorClockSynchronized: () => floorClock?.complete ?? false,
    onPoseSourceChange: (source) => {
      desiredPoseSource = source;
      floorPeer?.setPoseSource(source);
    },
  }) : undefined;
  const latencyHost = document.querySelector<HTMLElement>('[data-latency-hud]');
  if (latencyHost) mountLatencyHud(latencyHost, latency, () => ({
    offsetMs: floorClock?.offsetMs ?? 0,
    roundTripMs: floorClock?.roundTripMs ?? Number.POSITIVE_INFINITY,
    complete: floorClock?.complete ?? false,
  }));
  const setFloorRtcStats = mountRtcStatsHud(document.querySelector<HTMLElement>('[data-floor-rtc-stats]')!, 'Packed RTP · FLOOR inbound');
  try {
    const claim = await createRoom();
    const heading = document.querySelector('h1');
    if (heading) heading.innerHTML = `Join with <strong class="room-code">${claim.code}</strong>`;
    const status = document.querySelector<HTMLElement>('[data-status]');
    if (status) status.textContent = 'Open /camera and /tv on the other devices. This code closes in 10 minutes.';
    const picker = document.querySelector<HTMLElement>('[data-tv-picker]');
    if (picker) mountTvPicker(picker);
    let boardStarted = false;
    const socket = showConnection(claim, (message) => {
      floorClock?.handle(message);
      floorPeer?.handle(message);
      if (message.type === 'welcome' && !boardStarted) {
        boardStarted = true;
        const boardHost = document.querySelector<HTMLElement>('[data-floor-board]');
        const boardSession = connectBoard(claim);
        if (boardHost) mountBoardEditor(boardHost, boardSession.doc, {
          nowEpochMs: () => epochNow() + (floorClock?.offsetMs ?? 0),
          clockSynchronized: () => floorClock?.complete ?? false,
          onPaint: () => {
            const boardCanvas = boardHost.querySelector<HTMLCanvasElement>('[data-board-canvas]');
            if (boardCanvas) personStage?.setBoardCanvas(boardCanvas);
          },
        });
      }
      if (message.type === 'signal' && message.from === 'tv' && isEditPaintedMessage(message.data)) {
        const milliseconds = message.data.paintedEpochMs - message.data.originEpochMs;
        if (message.data.clockSynchronized) latency.add('edit', milliseconds);
        const output = document.querySelector<HTMLElement>('[data-edit-latency]');
        if (output) {
          if (message.data.clockSynchronized) output.dataset.lastEditLatencyMs = milliseconds.toFixed(3);
          output.textContent = message.data.clockSynchronized
            ? `TV painted edit ${message.data.editId.slice(0, 8)} · ${milliseconds.toFixed(1)} ms`
            : `TV painted edit ${message.data.editId.slice(0, 8)} · clocks were still synchronizing`;
        }
      }
      if (message.type === 'signal' && message.from === 'tv' && isTvSeatStatusMessage(message.data)) {
        const output = document.querySelector<HTMLElement>('[data-tv-seat-status]');
        if (output) {
          output.dataset.tvSeatStatus = message.data.status;
          output.textContent = message.data.status === 'paused' ? 'TV seat is asleep' : 'TV seat is awake';
        }
      }
      if (message.type === 'peer-left' && message.seat === 'tv') {
        const output = document.querySelector<HTMLElement>('[data-tv-seat-status]');
        if (output) {
          output.dataset.tvSeatStatus = 'left';
          output.textContent = 'TV seat is disconnected';
        }
      }
    });
    floorClock = new ClockSynchronizer(socket);
    floorPeer = new FloorPeer(socket, {
      onStream: (stream) => personStage?.setStream(stream),
      onPose: (frame) => personStage?.updatePose(frame),
      onInstallation: (installation) => {
        setCameraCalibration(installation);
        const readout = document.querySelector<HTMLElement>('[data-camera-pose]');
        const position = installation.camera.cameraPositionInTv;
        if (readout) readout.textContent = `${installation.installation.label} · ${position.x.toFixed(2)} m right, ${position.y.toFixed(2)} m up, ${position.z.toFixed(2)} m from TV · f ${installation.camera.focalLengthPx.toFixed(0)} px`;
        personStage?.setCalibration(installation);
      },
      onCaptureStatus: (statusMessage) => personStage?.setCaptureStatus(statusMessage),
      onPackedLayout: (layout) => personStage?.setPackedLayout(layout),
      onPoseSourcePending: (source) => personStage?.setPoseSourcePending(source),
      onPoseSourceApplied: (source) => personStage?.setPoseSourceApplied(source),
      onStopped: (reason) => personStage?.stop(reason),
      onStats: setFloorRtcStats,
    });
    floorPeer.setPoseSource(desiredPoseSource);
  } catch (error) {
    const status = document.querySelector<HTMLElement>('[data-status]');
    if (status) status.textContent = error instanceof Error ? error.message : 'Could not create room';
  }
}

function join(seat: 'camera' | 'tv'): void {
  shell(seat.toUpperCase(), `<div class="join-column">
    <form class="join-card">
      <label for="room-code">Room code</label>
      <input id="room-code" name="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" placeholder="000000" required />
      <button type="submit">Join as ${seat}</button>
      <p data-status>Enter the code shown on the FLOOR laptop.</p>
    </form>
    <div data-seat-tools></div>
  </div>`);
  const form = document.querySelector<HTMLFormElement>('form');
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = form.elements.namedItem('code') as HTMLInputElement;
    const status = document.querySelector<HTMLElement>('[data-status]');
    if (status) status.textContent = 'Joining…';
    void joinRoom(input.value, seat)
      .then((claim) => {
        form.querySelectorAll('input, button').forEach((element) => ((element as HTMLInputElement).disabled = true));
        if (status) status.textContent = `Connected to ${claim.code}`;
        if (seat === 'camera') {
          let capture: CameraCapture | undefined;
          let starting: Promise<CameraCapture> | undefined;
          let captureGeneration = 0;
          let latestInstallation: CameraCalibrationMessage | undefined;
          let cameraClock: ClockSynchronizer | undefined;
          const pendingSignals: SignalMessage[] = [];
          const socket = showConnection(claim, (message) => {
            cameraClock?.handle(message);
            if (capture) capture.handleSignalling(message);
            else pendingSignals.push(message);
          });
          cameraClock = new ClockSynchronizer(socket);
          const tools = document.querySelector<HTMLElement>('[data-seat-tools]');
          if (tools) mountCalibrationUi(tools, {
            onStreamStarted: (stream, video, setStatus, setDiagnostics, setPerformance, setRtcStats, setPublishSettings, targetFps, packedLayout, markGrantReady) => {
              const generation = ++captureGeneration;
              const startPromise = import('./media/cameraCapture.ts').then(({ CameraCapture: Capture }) =>
                Capture.start({
                  stream,
                  video,
                  socket,
                  onStatus: setStatus,
                  clockOffsetMs: () => cameraClock?.offsetMs ?? 0,
                  clockSynchronized: () => cameraClock?.complete ?? false,
                  onDiagnostics: setDiagnostics,
                  targetFps,
                  packedLayout,
                  onPerformance: setPerformance,
                  onRtcStats: setRtcStats,
                  onPublishSettings: setPublishSettings,
                  onGrantReady: markGrantReady,
                  onTransportReady: (started) => {
                    if (generation !== captureGeneration) {
                      started.stop();
                      return;
                    }
                    capture = started;
                    if (latestInstallation) started.sendInstallation(latestInstallation);
                    pendingSignals.splice(0).forEach((message) => started.handleSignalling(message));
                  },
                  onPoseSource: (source) => {
                    const output = tools.querySelector<HTMLElement>('[data-pose-transport]');
                    if (!output) return;
                    output.dataset.poseSource = source;
                    output.textContent = source === 'blocked'
                      ? 'Landmarks blocked until FLOOR selects a source.'
                      : source === 'laptop-rederives'
                        ? 'Laptop re-derives · matte track only; zero landmark packets leave CAMERA.'
                        : 'Landmarks use the private peer data channel; the server never receives them.';
                  },
                }));
              starting = startPromise;
              void startPromise.then((started) => {
                if (generation !== captureGeneration) started.stop();
                if (starting === startPromise) starting = undefined;
              }).catch((error: unknown) => {
                if (generation === captureGeneration) setStatus(error instanceof Error ? error.message : 'Camera processing failed');
                if (starting === startPromise) starting = undefined;
              });
            },
            onInstallation: (installation) => {
              latestInstallation = installation;
              capture?.sendInstallation(installation);
            },
            onGrantRequest: (tap) => {
              if (capture) {
                capture.requestGrant(tap);
                return;
              }
              const currentStart = starting;
              const generation = captureGeneration;
              void currentStart?.then((started) => {
                if (generation === captureGeneration && capture === started) started.requestGrant(tap);
              });
            },
            onStop: () => {
              captureGeneration += 1;
              const currentCapture = capture;
              const currentStart = starting;
              capture = undefined;
              starting = undefined;
              pendingSignals.length = 0;
              currentCapture?.stop();
              void currentStart?.then((started) => started.stop());
            },
            onTargetFpsChange: (targetFps) => {
              capture?.setTargetFps(targetFps);
              const currentStart = starting;
              const generation = captureGeneration;
              void currentStart?.then((started) => {
                if (generation === captureGeneration && capture === started) started.setTargetFps(targetFps);
              });
            },
          });
        } else {
          let tvStarted = false;
          let tvClock: ClockSynchronizer | undefined;
          let socket!: WebSocket;
          socket = showConnection(claim, (message) => {
            tvClock?.handle(message);
            if (message.type !== 'welcome' || tvStarted) return;
            tvStarted = true;
            form.hidden = true;
            const tools = document.querySelector<HTMLElement>('[data-seat-tools]');
            if (!tools) return;
            tools.classList.add('tv-board-host');
            const boardSession = connectBoard(claim);
            mountTvBoard(tools, boardSession.doc, claim.code, (markers) => {
              for (const marker of markers) {
                if (marker.originSeat !== 'floor') continue;
                const clockSynchronized = marker.clockSynchronized && (tvClock?.complete ?? false);
                relaySignal(socket, 'floor', {
                  kind: 'edit-painted',
                  editId: marker.editId,
                  originEpochMs: marker.capturedEpochMs,
                  paintedEpochMs: epochNow() + (tvClock?.offsetMs ?? 0),
                  clockSynchronized,
                });
              }
            });
            const surface = tools.querySelector<HTMLElement>('.tv-surface')!;
            const wakeLockApi = (navigator as Navigator & {
              wakeLock?: { request(type: 'screen'): Promise<WakeLockHandle> };
            }).wakeLock;
            const displayLifecycle = startDisplaySeatLifecycle({
              visibility: document,
              requestWakeLock: wakeLockApi ? () => wakeLockApi.request('screen') : undefined,
              sendStatus: (status) => {
                surface.dataset.tvSeatStatus = status;
                const payload: TvSeatStatusMessage = { kind: 'seat-status', seat: 'tv', status };
                relaySignal(socket, 'floor', payload);
              },
              onWakeLockState: (state) => { surface.dataset.wakeLock = state; },
            });
            window.addEventListener('pagehide', () => displayLifecycle.stop(), { once: true });
          });
          tvClock = new ClockSynchronizer(socket);
        }
      })
      .catch((error: unknown) => {
        if (status) status.textContent = error instanceof Error ? error.message : 'Could not join';
      });
  });
}

if (route === '/floor') void floor();
else if (route === '/camera') join('camera');
else if (route === '/tv') join('tv');
else {
  shell('THREE SEATS', `
    <div class="hero">
      <p class="eyebrow">STANDALONE PROTOTYPE V0</p>
      <h1>A real room, reflected live.</h1>
      <p>Start on the laptop. Then join the same short-lived room from the camera and the physical TV.</p>
      <nav><a class="primary" href="/floor">Open FLOOR</a><a href="/camera">CAMERA</a><a href="/tv">TV</a></nav>
    </div>`);
}

export const currentSeat: Seat | undefined = route === '/floor' ? 'floor' : route === '/camera' ? 'camera' : route === '/tv' ? 'tv' : undefined;
